import { useExportLoadingBridge } from 'features/pdfExport';
import { PdfExportTargets } from 'pages/Valuations/util/constants';

/**
 * PDF export v2 hidden iframes call `waitForReadiness` before `ValuationApproaches`
 * mounts. Summary targets already publish `loading` on `document.body` via
 * `useExportSignal` in `ValuationsAllocation`; approach-only targets did not, so
 * the capture pipeline hit the rAF "missing" fallback while `GridSkeleton` was
 * still showing (no `[data-ref]` yet). Bridge the same contract for every
 * approach-tab export target until outer valuation data has loaded.
 *
 * Uses {@link useExportLoadingBridge} (not `useExportSignal`) because React
 * commits child effects before parent effects: when the outer loading flag
 * flips, `ValuationApproaches`'s real emitter has already set `body[…]='ready'`,
 * and a `useExportSignal`-based bridge would then run its cleanup and erase
 * that value, sending the orchestrator into the missing-fallback timer.
 */
export const useBridgeApproachPdfExportSignals = (isExportMode: boolean, isOuterLoading: boolean) => {
  const enabled = isExportMode && isOuterLoading;

  useExportLoadingBridge({ target: PdfExportTargets.BACKSOLVE.id, enabled });
  useExportLoadingBridge({ target: PdfExportTargets.BACKSOLVE_MARKET_ADJUSTMENT_LEDGER.id, enabled });
  useExportLoadingBridge({ target: PdfExportTargets.EXTERNAL_VALUATION.id, enabled });
  useExportLoadingBridge({ target: PdfExportTargets.PUBLIC_COMPS.id, enabled });
  useExportLoadingBridge({ target: PdfExportTargets.PUBLIC_COMPS_DESCRIPTIONS.id, enabled });
  useExportLoadingBridge({ target: PdfExportTargets.DISCOUNTED_CASH_FLOW.id, enabled });
  useExportLoadingBridge({ target: PdfExportTargets.FUTURE_EXIT.id, enabled });
  useExportLoadingBridge({ target: PdfExportTargets.TRANSACTION_COMPS.id, enabled });
  useExportLoadingBridge({ target: PdfExportTargets.TRANSACTION_COMPS_DESCRIPTIONS.id, enabled });
  useExportLoadingBridge({ target: PdfExportTargets.SPECIFIED_SHARE_VALUES.id, enabled });
  useExportLoadingBridge({ target: PdfExportTargets.CALIBRATION.id, enabled });
};
